import React, { useEffect, useRef, useState } from 'react';
import {
    Box,
    Container,
    Paper,
    Typography,
    Chip,
    IconButton,
    TextField,
    Stack,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
    useTheme,
    useMediaQuery,
    Avatar,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Collapse,
    Alert,
    Card,
    CardContent,
    CardActions,
    Tooltip,
    Tabs,
    Tab,
    Grid,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    FormControlLabel,
    Checkbox,
    Skeleton
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import ChecklistIcon from '@mui/icons-material/Checklist';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import DeleteIcon from '@mui/icons-material/Delete';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import LocalMallIcon from '@mui/icons-material/LocalMall';
import PrintIcon from '@mui/icons-material/Print';
import apiClient from '../api';
import { formatTarpCell, formatTarpTotal, sumTarps } from '../utils/tarpMetrics';
import { formatTotalWeeklyMiles, getTotalWeeklyMiles, mileageRowTruckLabel } from '../utils/weeklyChecklistMiles';
import { FlagFocus, primarySpot } from '../utils/flagFocus';
import ReceiptAttachmentGallery from '../components/forms/ReceiptAttachmentGallery';
import { WeeklyChecklistPrintDialog } from '../components/forms/WeeklyChecklistPrint';
import { useAuth } from '../context/AuthContext';
import { formatDateTimeCentral } from '../utils/constants';
import { isOfficeTimeOffRequest } from '../utils/personnelType';
import { useLocation, useNavigate } from 'react-router-dom';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

function TabPanel({ children, value, index, screen }) {
    return (
        <div role="tabpanel" hidden={value !== index} data-usage-screen={screen || undefined}>
            {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
        </div>
    );
}

// Helper function to convert request type codes to friendly labels
const getRequestTypeLabel = (requestType) => {
    const labels = {
        'sick_doctor': 'Sick/Doctor',
        'vacation_paid': 'Vacation-Paid',
        'bereavement': 'Bereavement',
        'unpaid': 'Time Off Without Pay',
        'military': 'Military',
        'jury_duty': 'Jury Duty',
        'maternity_paternity': 'Maternity/Paternity',
        'other': 'Other',
        // Legacy support for old values
        'vacation': 'Vacation',
        'sick_leave': 'Sick Leave',
        'personal': 'Personal',
        'emergency': 'Emergency'
    };
    return labels[requestType] || requestType;
};

function FormsManagementPage() {
    const { user } = useAuth();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [tabValue, setTabValue] = useState(0);
    const location = useLocation();
    const navigate = useNavigate();
    const selectTab = (next) => {
        setTabValue(next);
        const params = new URLSearchParams(location.search || '');
        params.set('tab', String(next));
        navigate({ pathname: '/forms-management', search: `?${params.toString()}` }, { replace: true });
    };
    const [timeOffRequests, setTimeOffRequests] = useState([]);
    const [apparelOrders, setApparelOrders] = useState([]);
    const [checklists, setChecklists] = useState([]);
    const [openFlags, setOpenFlags] = useState([]);
    const [loading, setLoading] = useState(false);
    const [ready, setReady] = useState(false);
    // Global search removed from top; per-tab filters below
    const [search, setSearch] = useState('');
    // Time Off filters
    const [tfSearch, setTfSearch] = useState('');
    const [tfStatus, setTfStatus] = useState('all');
    const [tfType, setTfType] = useState('all');
    const [tfDateFrom, setTfDateFrom] = useState(null);
    const [tfDateTo, setTfDateTo] = useState(null);
    // Apparel filters
    const [apSearch, setApSearch] = useState('');
    const [apStatus, setApStatus] = useState('all');
    const [apNewOnly, setApNewOnly] = useState(false);
    // Weekly filters
    const [wkSearch, setWkSearch] = useState('');
    const [wkReviewed, setWkReviewed] = useState('all');
    const [wkDateFrom, setWkDateFrom] = useState(null);
    const [wkDateTo, setWkDateTo] = useState(null);
    const [error, setError] = useState(null);

    const [printOpen, setPrintOpen] = useState(false);
    const [printSeed, setPrintSeed] = useState(null);
    const [detailOpen, setDetailOpen] = useState(false);
    const [detailItem, setDetailItem] = useState(null);
    const [detailType, setDetailType] = useState(null);

    // Edit decision dialog state
    const [decisionDialogOpen, setDecisionDialogOpen] = useState(false);
    const [decisionTarget, setDecisionTarget] = useState(null); // {_id, status}
    const [decisionStatus, setDecisionStatus] = useState('pending');
    const [decisionNotes, setDecisionNotes] = useState('');

    const [deleteTimeOffDialogOpen, setDeleteTimeOffDialogOpen] = useState(false);
    const [deleteTimeOffTarget, setDeleteTimeOffTarget] = useState(null); // { id, label }
    const [deleteTimeOffSubmitting, setDeleteTimeOffSubmitting] = useState(false);

  // Apparel flexible status change dialog
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [statusTarget, setStatusTarget] = useState(null); // order object
  const [statusNew, setStatusNew] = useState('submitted');

    // Track seen items to suppress "New" chip and highlight after viewed/actioned
    const [seen, setSeen] = useState({ timeoff: {}, apparel: {}, checklist: {} });

    useEffect(() => {
        try {
            const t = JSON.parse(localStorage.getItem('forms.seen.timeoff') || '{}');
            const a = JSON.parse(localStorage.getItem('forms.seen.apparel') || '{}');
            const c = JSON.parse(localStorage.getItem('forms.seen.checklist') || '{}');
            setSeen({ timeoff: t, apparel: a, checklist: c });
        } catch (_) {}
    }, []);

    const markSeen = (type, id) => {
        if (!id) return;
        setSeen(prev => {
            const updated = { ...prev, [type]: { ...(prev[type] || {}), [id]: true } };
            try { localStorage.setItem(`forms.seen.${type}`, JSON.stringify(updated[type])); } catch (_) {}
            return updated;
        });
    };

    const showNewBadge = (type, id, createdAt) => {
        if (!id) return false;
        if (seen[type]?.[id]) return false;
        return isNewEntry(createdAt);
    };

    // Helper: consider items created within last 24 hours as New
    const isNewEntry = (dateLike) => {
        if (!dateLike) return false;
        const t = new Date(dateLike).getTime();
        return Number.isFinite(t) && (Date.now() - t) <= 24*60*60*1000;
    };

    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            const [timeOffRes, apparelRes, checklistRes, flagRes] = await Promise.all([
                apiClient.get('/forms/time-off'),
                apiClient.get('/forms/apparel'),
                apiClient.get('/forms/weekly-checklist'),
                apiClient.get('/anomalies', { params: { status: 'open' } }).catch(() => ({ data: { data: [] } })),
            ]);

            setTimeOffRequests(timeOffRes.data.data || []);
            setApparelOrders(apparelRes.data.data || []);
            setChecklists(checklistRes.data.data || []);
            setOpenFlags(flagRes.data?.data || []);
        } catch (err) {
            console.error('Error fetching forms data:', err);
            setError('Failed to load submissions. Please try again.');
        } finally {
            setLoading(false);
            setReady(true);
        }
    };

    const renderSkeletonRows = (columns) => (
        Array.from({ length: 6 }, (_, row) => (
            <TableRow key={`skeleton-${row}`}>
                {Array.from({ length: columns }, (__, col) => (
                    <TableCell key={col}>
                        <Skeleton variant="text" width={col === 0 ? '72%' : '48%'} />
                    </TableCell>
                ))}
            </TableRow>
        ))
    );

    useEffect(() => {
        fetchData();
    }, []);

    // Support deep-linking to a specific tab: /forms-management?tab=0|1|2
    useEffect(() => {
        try {
            const params = new URLSearchParams(location.search || '');
            const t = params.get('tab');
            if (t !== null && !Number.isNaN(Number(t))) {
                setTabValue(Number(t));
            }
        } catch (_) {}
    }, [location.search]);

    const openDeleteTimeOffDialog = (request) => {
        const name = request.submittedBy?.name || 'employee';
        const range = `${new Date(request.startDate).toLocaleDateString()} – ${new Date(request.endDate).toLocaleDateString()}`;
        setDeleteTimeOffTarget({
            id: request._id,
            label: `the time off request for ${name} (${range})`,
        });
        setDeleteTimeOffDialogOpen(true);
    };

    const closeDeleteTimeOffDialog = () => {
        setDeleteTimeOffDialogOpen(false);
        setDeleteTimeOffTarget(null);
    };

    const confirmDeleteTimeOff = async () => {
        if (!deleteTimeOffTarget?.id) return;
        setDeleteTimeOffSubmitting(true);
        try {
            await apiClient.delete(`/forms/time-off/${deleteTimeOffTarget.id}`);
            if (detailItem?._id === deleteTimeOffTarget.id) {
                setDetailOpen(false);
                setDetailItem(null);
                setDetailType(null);
            }
            closeDeleteTimeOffDialog();
            fetchData();
        } catch (err) {
            console.error('Error deleting time off request:', err);
            setError(err?.response?.data?.message || 'Failed to delete time off request.');
        } finally {
            setDeleteTimeOffSubmitting(false);
        }
    };

    const handleUpdateTimeOff = async (id, status, reviewNotes = '') => {
        try {
            console.warn('[FormsManagement] Submitting review (PUT)', { id, status, hasNotes: Boolean(reviewNotes) });
            const res = await apiClient.put(`/forms/time-off/${id}`, { status, reviewNotes });
            console.warn('[FormsManagement] Review response (PUT)', res?.status, res?.data);
            fetchData();
            if (detailItem && detailItem._id === id) {
                setDetailItem({ ...detailItem, status });
            }
        } catch (err) {
            console.error('[FormsManagement] Error reviewing request (PUT):', err?.message, err?.response?.status, err?.response?.data);
            if (err?.code === 'ERR_NETWORK') {
                try {
                    console.warn('[FormsManagement] Retrying review with POST /review alias', { id, status });
                    const res2 = await apiClient.post(`/forms/time-off/${id}/review`, { status, reviewNotes });
                    console.warn('[FormsManagement] Review response (POST)', res2?.status, res2?.data);
                    fetchData();
                    if (detailItem && detailItem._id === id) {
                        setDetailItem({ ...detailItem, status });
                    }
                    return;
                } catch (err2) {
                    console.error('[FormsManagement] Fallback POST failed:', err2?.message, err2?.response?.status, err2?.response?.data);
                    setError(err2.response?.data?.message || 'Failed to update request.');
                    return;
                }
            }
            setError(err.response?.data?.message || 'Failed to update request.');
        }
    };

    const openDecisionDialog = (request) => {
        setDecisionTarget({ _id: request._id, currentStatus: request.status });
        setDecisionStatus(request.status);
        setDecisionNotes('');
        setDecisionDialogOpen(true);
    };

    const submitDecisionChange = async () => {
        if (!decisionTarget) return;
        try {
            await handleUpdateTimeOff(decisionTarget._id, decisionStatus, decisionNotes);
            setDecisionDialogOpen(false);
            setDecisionTarget(null);
        } catch (e) {
            // handleUpdateTimeOff sets error
        }
    };

    const handleUpdateApparel = async (id, updates) => {
        try {
            await apiClient.put(`/forms/apparel/${id}`, updates);
            fetchData();
            if (detailItem && detailItem._id === id) {
                setDetailItem({ ...detailItem, ...updates });
            }
        } catch (err) {
            console.error('Error updating apparel order:', err);
            setError('Failed to update order.');
        }
    };

    const handleCardClick = (item, type) => {
        setDetailItem(item);
        setDetailType(type);
        setDetailOpen(true);
    };

    const flaggedSourceIds = new Set(openFlags.map((flag) => `${flag.source}:${flag.sourceId}`));
    const openedFlagId = useRef('');

    useEffect(() => {
        if (loading) return;
        const params = new URLSearchParams(location.search || '');
        const id = params.get('id');
        if (!id || openedFlagId.current === id) return;
        const tab = params.get('tab');
        if (tab === '2') {
            const row = checklists.find((checklist) => String(checklist._id) === id);
            if (!row) return;
            openedFlagId.current = id;
            handleCardClick(row, 'checklist');
            return;
        }
        if (tab === '0' || tab == null) {
            const row = timeOffRequests.find((request) => String(request._id) === id);
            if (!row) return;
            openedFlagId.current = id;
            handleCardClick(row, 'timeoff');
        }
    }, [loading, location.search, checklists, timeOffRequests]);

    const renderStatsCards = () => {
        const timeOffStats = {
            pending: timeOffRequests.filter(r => r.status === 'pending').length,
            approved: timeOffRequests.filter(r => r.status === 'approved').length,
            denied: timeOffRequests.filter(r => r.status === 'denied').length
        };

        const apparelStats = {
            submitted: apparelOrders.filter(o => o.status === 'submitted').length,
            processing: apparelOrders.filter(o => o.status === 'processing').length,
            shipped: apparelOrders.filter(o => o.status === 'shipped').length
        };

        const checklistStats = {
            reviewed: checklists.filter(c => Boolean(c.reviewedBy)).length,
            pending: checklists.filter(c => !c.reviewedBy).length
        };

        return (
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} md={4}>
                    <Card onClick={() => selectTab(0)} sx={{ cursor: 'pointer' }}>
                        <CardContent>
                            <Stack direction="row" alignItems="center" spacing={2}>
                                <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                                    <EventAvailableIcon />
                                </Avatar>
                                <Box>
                                    {ready ? (
                                        <Typography variant="h4">{timeOffRequests.length}</Typography>
                                    ) : (
                                        <Skeleton variant="text" width={72} height={40} />
                                    )}
                                    <Typography variant="body2" color="text.secondary">Time Off Requests</Typography>
                                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                                        {ready ? (
                                            <>
                                                <Chip label={`${timeOffStats.pending} Pending`} size="small" color="warning" />
                                                <Chip label={`${timeOffStats.approved} Approved`} size="small" color="success" />
                                            </>
                                        ) : (
                                            <>
                                                <Skeleton variant="rounded" width={88} height={24} />
                                                <Skeleton variant="rounded" width={96} height={24} />
                                            </>
                                        )}
                                    </Stack>
                                </Box>
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Card onClick={() => selectTab(1)} sx={{ cursor: 'pointer' }}>
                        <CardContent>
                            <Stack direction="row" alignItems="center" spacing={2}>
                                <Avatar sx={{ bgcolor: theme.palette.secondary.main }}>
                                    <LocalMallIcon />
                                </Avatar>
                                <Box>
                                    {ready ? (
                                        <Typography variant="h4">{apparelOrders.length}</Typography>
                                    ) : (
                                        <Skeleton variant="text" width={72} height={40} />
                                    )}
                                    <Typography variant="body2" color="text.secondary">Apparel Orders</Typography>
                                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                                        {ready ? (
                                            <>
                                                <Chip label={`${apparelStats.submitted} New`} size="small" color="info" />
                                                <Chip label={`${apparelStats.processing} Processing`} size="small" color="warning" />
                                            </>
                                        ) : (
                                            <>
                                                <Skeleton variant="rounded" width={64} height={24} />
                                                <Skeleton variant="rounded" width={104} height={24} />
                                            </>
                                        )}
                                    </Stack>
                                </Box>
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Card onClick={() => selectTab(2)} sx={{ cursor: 'pointer' }}>
                        <CardContent>
                            <Stack direction="row" alignItems="center" spacing={2}>
                                <Avatar sx={{ bgcolor: theme.palette.success.main }}>
                                    <ChecklistIcon />
                                </Avatar>
                                <Box>
                                    {ready ? (
                                        <Typography variant="h4">{checklists.length}</Typography>
                                    ) : (
                                        <Skeleton variant="text" width={72} height={40} />
                                    )}
                                    <Typography variant="body2" color="text.secondary">Weekly Checklists</Typography>
                                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                                        {ready ? (
                                            <>
                                                <Chip label={`${checklistStats.reviewed} Reviewed`} size="small" color="success" />
                                                <Chip label={`${checklistStats.pending} Pending Review`} size="small" color="warning" />
                                            </>
                                        ) : (
                                            <>
                                                <Skeleton variant="rounded" width={96} height={24} />
                                                <Skeleton variant="rounded" width={128} height={24} />
                                            </>
                                        )}
                                    </Stack>
                                </Box>
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        );
    };

    const renderTimeOffTable = () => {
        const filteredRequests = timeOffRequests.filter(req => {
            const text = tfSearch.trim().toLowerCase();
            const matchesText = !text || [
                req.submittedBy?.name,
                req.submittedBy?.email,
                req.requestType,
                req.reason
            ].filter(Boolean).some(v => String(v).toLowerCase().includes(text));
            const matchesStatus = tfStatus === 'all' || req.status === tfStatus;
            const matchesType = tfType === 'all' || req.requestType === tfType;
            const startTs = new Date(req.startDate).getTime();
            const fromOk = !tfDateFrom || startTs >= new Date(tfDateFrom).setHours(0,0,0,0);
            const toOk = !tfDateTo || startTs <= new Date(tfDateTo).setHours(23,59,59,999);
            return matchesText && matchesStatus && matchesType && fromOk && toOk;
        });

        return (
            <>
            <Stack spacing={1.5} sx={{ mb: 2 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    <TextField size="small" placeholder="Search name, email, reason..." value={tfSearch} onChange={(e)=>setTfSearch(e.target.value)} sx={{ flex: 1 }} />
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel>Status</InputLabel>
                        <Select label="Status" value={tfStatus} onChange={(e)=>setTfStatus(e.target.value)}>
                            <MenuItem value="all">All</MenuItem>
                            <MenuItem value="pending">Pending</MenuItem>
                            <MenuItem value="approved">Approved</MenuItem>
                            <MenuItem value="denied">Denied</MenuItem>
                            <MenuItem value="withdrawn">Withdrawn</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel>Type</InputLabel>
                        <Select label="Type" value={tfType} onChange={(e)=>setTfType(e.target.value)}>
                            <MenuItem value="all">All</MenuItem>
                            <MenuItem value="sick_doctor">Sick/Doctor</MenuItem>
                            <MenuItem value="vacation_paid">Vacation-Paid</MenuItem>
                            <MenuItem value="bereavement">Bereavement</MenuItem>
                            <MenuItem value="unpaid">Time Off Without Pay</MenuItem>
                            <MenuItem value="military">Military</MenuItem>
                            <MenuItem value="jury_duty">Jury Duty</MenuItem>
                            <MenuItem value="maternity_paternity">Maternity/Paternity</MenuItem>
                            <MenuItem value="other">Other</MenuItem>
                        </Select>
                    </FormControl>
                </Stack>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                        <DatePicker label="Start from" value={tfDateFrom} onChange={setTfDateFrom} slotProps={{ textField: { size: 'small', fullWidth: true } }} />
                        <DatePicker label="Start to" value={tfDateTo} onChange={setTfDateTo} slotProps={{ textField: { size: 'small', fullWidth: true } }} />
                        <Button size="small" onClick={()=>{ setTfSearch(''); setTfStatus('all'); setTfType('all'); setTfDateFrom(null); setTfDateTo(null); }}>Clear Filters</Button>
                    </Stack>
                </LocalizationProvider>
            </Stack>
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Employee</TableCell>
                            <TableCell>Request Type</TableCell>
                            <TableCell>Start Date</TableCell>
                            <TableCell>End Date</TableCell>
                            <TableCell>Yard</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Submitted</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {!ready ? renderSkeletonRows(8) : filteredRequests.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={8} align="center">
                                    <Typography color="text.secondary">No time off requests found</Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            filteredRequests.map((request) => (
                                <TableRow 
                                    key={request._id}
                                    hover
                                    onClick={() => { markSeen('timeoff', request._id); handleCardClick(request, 'timeoff'); }}
                                    sx={{ 
                                        cursor: 'pointer',
                                        borderLeft: showNewBadge('timeoff', request._id, request.createdAt) || request.status === 'pending' || request.cancellationRequested ? '4px solid' : '4px solid transparent',
                                        borderLeftColor: (request.status === 'pending' || request.cancellationRequested) ? 'warning.main' : (showNewBadge('timeoff', request._id, request.createdAt) ? 'info.main' : 'transparent'),
                                        bgcolor: (request.status === 'pending' || request.cancellationRequested) ? 'warning.50' : (showNewBadge('timeoff', request._id, request.createdAt) ? 'info.50' : 'inherit')
                                    }}
                                >
                                    <TableCell>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <Avatar sx={{ width: 32, height: 32 }}>
                                                {request.submittedBy?.name?.[0] || '?'}
                                            </Avatar>
                                            <Box>
                                                <Typography variant="body2">{request.submittedBy?.name || 'Unknown'}</Typography>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Typography variant="caption" color="text.secondary">{request.submittedBy?.email}</Typography>
                                                {showNewBadge('timeoff', request._id, request.createdAt) && (
                                                    <Chip label="New" size="small" color="info" />
                                                )}
                                                {flaggedSourceIds.has(`time_off:${request._id}`) && (
                                                    <Chip label="Needs a look" size="small" color="warning" />
                                                )}
                                            </Stack>
                                            </Box>
                                        </Stack>
                                    </TableCell>
                                    <TableCell>
                                        <Chip label={getRequestTypeLabel(request.requestType)} size="small" variant="outlined" />
                                    </TableCell>
                                    <TableCell>
                                        {new Date(request.startDate).toLocaleDateString()}
                                        {request.isPartialDay && request.partialDayStartTime && (
                                            <Typography variant="caption" display="block" color="text.secondary">
                                                {request.partialDayStartTime} - {request.partialDayEndTime}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {request.isPartialDay ? (
                                            <Typography variant="body2" color="text.secondary">
                                                {request.partialDayHours} hour{request.partialDayHours !== 1 ? 's' : ''}
                                            </Typography>
                                        ) : (
                                            new Date(request.endDate).toLocaleDateString()
                                        )}
                                    </TableCell>
                                    <TableCell sx={{ maxWidth: 200 }}>
                                        {isOfficeTimeOffRequest(request) ? (
                                            request.isPartialDay && request.partialDayStartTime ? (
                                                <Stack spacing={0.25}>
                                                    <Typography variant="caption" display="block">
                                                        Leave at: {request.partialDayStartTime}
                                                    </Typography>
                                                    <Typography variant="caption" display="block">
                                                        Back at: {request.partialDayEndTime}
                                                    </Typography>
                                                </Stack>
                                            ) : (
                                                <Typography variant="caption" color="text.secondary">All Day</Typography>
                                            )
                                        ) : request.leavingYardAt || request.backToYardAt ? (
                                            <Stack spacing={0.25}>
                                                <Typography variant="caption" display="block" noWrap title={request.leavingYardAt ? formatDateTimeCentral(request.leavingYardAt) : ''}>
                                                    Out: {request.leavingYardAt ? formatDateTimeCentral(request.leavingYardAt) : '—'}
                                                </Typography>
                                                <Typography variant="caption" display="block" noWrap title={request.backToYardAt ? formatDateTimeCentral(request.backToYardAt) : ''}>
                                                    In: {request.backToYardAt ? formatDateTimeCentral(request.backToYardAt) : '—'}
                                                </Typography>
                                            </Stack>
                                        ) : (
                                            <Typography variant="caption" color="text.secondary">—</Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                            <Chip 
                                                label={request.status} 
                                                size="small" 
                                                color={
                                                    request.status === 'approved' ? 'success' :
                                                    request.status === 'denied' ? 'error' :
                                                    request.status === 'withdrawn' ? 'default' :
                                                    'warning'
                                                }
                                            />
                                            {request.cancellationRequested && (
                                                <Chip 
                                                    label="Needs Review" 
                                                    size="small" 
                                                    color="warning" 
                                                    variant="outlined"
                                                />
                                            )}
                                        </Stack>
                                    </TableCell>
                                    <TableCell>{new Date(request.createdAt).toLocaleDateString()}</TableCell>
                                    <TableCell align="right">
                                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                                            {request.status === 'pending' ? (
                                                <>
                                                    <Tooltip title="Approve">
                                                        <IconButton 
                                                            size="small" 
                                                            color="success"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleUpdateTimeOff(request._id, 'approved');
                                                            }}
                                                        >
                                                            <CheckCircleIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Deny">
                                                        <IconButton 
                                                            size="small" 
                                                            color="error"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleUpdateTimeOff(request._id, 'denied');
                                                            }}
                                                        >
                                                            <CancelIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                </>
                                            ) : (
                                                <Stack direction="row" spacing={1}>
                                                    <Tooltip title="Change decision">
                                                        <IconButton
                                                            size="small"
                                                            color="primary"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                openDecisionDialog(request);
                                                            }}
                                                        >
                                                            <EditIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                    {request.cancellationRequested && (
                                                        <>
                                                            <Tooltip title="Approve cancellation (revert to pending)">
                                                                <Button
                                                                    size="small"
                                                                    color="warning"
                                                                    variant="outlined"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleUpdateTimeOff(request._id, 'pending', 'Cancellation approved by management');
                                                                    }}
                                                                >
                                                                    Approve Cancel
                                                                </Button>
                                                            </Tooltip>
                                                            <Tooltip title="Dismiss cancellation request">
                                                                <Button
                                                                    size="small"
                                                                    variant="outlined"
                                                                    onClick={async (e) => {
                                                                        e.stopPropagation();
                                                                        try {
                                                                            await apiClient.post(`/forms/time-off/${request._id}/cancellation/clear`, { reviewNotes: 'Cancellation declined by management' });
                                                                            fetchData();
                                                                        } catch (err) {
                                                                            console.error('Error clearing cancellation request:', err);
                                                                        }
                                                                    }}
                                                                >
                                                                    Dismiss
                                                                </Button>
                                                            </Tooltip>
                                                        </>
                                                    )}
                                                </Stack>
                                            )}
                                            <Tooltip title="Delete request">
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        openDeleteTimeOffDialog(request);
                                                    }}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            </>
        );
    };

    const renderApparelTable = () => {
        const filteredOrders = apparelOrders.filter(order => {
            const text = apSearch.trim().toLowerCase();
            const matchesText = !text || [
                order.submittedBy?.name,
                order.submittedBy?.email,
                order.status,
                order.trackingNumber
            ].filter(Boolean).some(v => String(v).toLowerCase().includes(text));
            const matchesStatus = apStatus === 'all' || order.status === apStatus;
            const matchesNew = !apNewOnly || showNewBadge('apparel', order._id, order.createdAt);
            return matchesText && matchesStatus && matchesNew;
        });

        return (
            <>
            <Stack spacing={1.5} sx={{ mb: 2 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    <TextField size="small" placeholder="Search name, email, status, tracking..." value={apSearch} onChange={(e)=>setApSearch(e.target.value)} sx={{ flex: 1 }} />
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel>Status</InputLabel>
                        <Select label="Status" value={apStatus} onChange={(e)=>setApStatus(e.target.value)}>
                            <MenuItem value="all">All</MenuItem>
                            <MenuItem value="submitted">Submitted</MenuItem>
                            <MenuItem value="processing">Processing</MenuItem>
                            <MenuItem value="shipped">Shipped</MenuItem>
                            <MenuItem value="delivered">Delivered</MenuItem>
                            <MenuItem value="cancelled">Cancelled</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControlLabel control={<Checkbox checked={apNewOnly} onChange={(e)=>setApNewOnly(e.target.checked)} />} label="New only (24h)" />
                    <Button size="small" onClick={()=>{ setApSearch(''); setApStatus('all'); setApNewOnly(false); }}>Clear Filters</Button>
                </Stack>
            </Stack>
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Employee</TableCell>
                            <TableCell>Items</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Submitted</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {!ready ? renderSkeletonRows(5) : filteredOrders.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={5} align="center">
                                    <Typography color="text.secondary">No apparel orders found</Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            filteredOrders.map((order) => (
                                <TableRow 
                                    key={order._id}
                                    hover
                                    onClick={() => { markSeen('apparel', order._id); handleCardClick(order, 'apparel'); }}
                                    sx={{ 
                                        cursor: 'pointer',
                                        borderLeft: (order.status === 'submitted' || showNewBadge('apparel', order._id, order.createdAt)) ? '4px solid' : '4px solid transparent',
                                        borderLeftColor: order.status === 'submitted' ? 'warning.main' : (showNewBadge('apparel', order._id, order.createdAt) ? 'info.main' : 'transparent'),
                                        bgcolor: order.status === 'submitted' ? 'warning.50' : (showNewBadge('apparel', order._id, order.createdAt) ? 'info.50' : 'inherit')
                                    }}
                                >
                                    <TableCell>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <Avatar sx={{ width: 32, height: 32 }}>
                                                {order.submittedBy?.name?.[0] || '?'}
                                            </Avatar>
                                            <Box>
                                                <Typography variant="body2">{order.submittedBy?.name || 'Unknown'}</Typography>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                <Typography variant="caption" color="text.secondary">{order.submittedBy?.email}</Typography>
                                                    {showNewBadge('apparel', order._id, order.createdAt) && (
                                                        <Chip label="New" size="small" color="info" />
                                                    )}
                                                </Stack>
                                            </Box>
                                        </Stack>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">{order.items?.length || 0} items</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                        <Chip 
                                            label={order.status} 
                                            size="small" 
                                            color={
                                                order.status === 'delivered' ? 'success' :
                                                order.status === 'shipped' ? 'info' :
                                                order.status === 'processing' ? 'warning' :
                                                'default'
                                            }
                                        />
                                            {order.status === 'submitted' && (
                                                <Chip label="Needs Attention" size="small" color="warning" variant="outlined" />
                                            )}
                                        </Stack>
                                    </TableCell>
                                    <TableCell>{new Date(order.createdAt).toLocaleString()}</TableCell>
                                    <TableCell align="right">
                                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                                        {order.status === 'submitted' && (
                                                <>
                                                    <Tooltip title="Mark Processing">
                                                <IconButton 
                                                    size="small" 
                                                    color="primary"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleUpdateApparel(order._id, { status: 'processing' });
                                                    }}
                                                >
                                                    <HourglassEmptyIcon />
                                                </IconButton>
                                            </Tooltip>
                                                    <Tooltip title="Cancel Order">
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleUpdateApparel(order._id, { status: 'cancelled' });
                                                            }}
                                                        >
                                                            <CancelIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Delete">
                                                        <IconButton
                                                            size="small"
                                                            onClick={async (e) => {
                                                                e.stopPropagation();
                                                                try {
                                                                    await apiClient.delete(`/forms/apparel/${order._id}`);
                                                                    fetchData();
                                                                } catch (err) {
                                                                    console.error('Delete failed', err);
                                                                    setError('Failed to delete order.');
                                                                }
                                                            }}
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                </>
                                            )}

                                            {order.status === 'processing' && (
                                                <>
                                                    <Tooltip title="Mark Shipped">
                                                        <IconButton
                                                            size="small"
                                                            color="info"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleUpdateApparel(order._id, { status: 'shipped' });
                                                            }}
                                                        >
                                                            <LocalShippingIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Cancel Order">
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleUpdateApparel(order._id, { status: 'cancelled' });
                                                            }}
                                                        >
                                                            <CancelIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                </>
                                            )}

                                            {order.status === 'shipped' && (
                                                <Tooltip title="Mark Delivered">
                                                    <IconButton
                                                        size="small"
                                                        color="success"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleUpdateApparel(order._id, { status: 'delivered' });
                                                        }}
                                                    >
                                                        <CheckCircleIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            )}

                                            {order.status === 'cancelled' && (
                                                <Tooltip title="Reopen (Submitted)">
                                                    <IconButton
                                                        size="small"
                                                        color="primary"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleUpdateApparel(order._id, { status: 'submitted' });
                                                        }}
                                                    >
                                                        <HourglassEmptyIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            )}

                                            {/* Flexible change status action */}
                                            <Tooltip title="Change Status">
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setStatusTarget(order);
                                                        setStatusNew(order.status);
                                                        setStatusDialogOpen(true);
                                                    }}
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            </>
        );
    };

    const renderChecklistTable = () => {
        const filteredChecklists = checklists.filter(checklist => {
            const text = wkSearch.trim().toLowerCase();
            const matchesText = !text || [
                checklist.submittedBy?.name,
                checklist.submittedBy?.email,
                checklist.truck?.unitNumber,
                checklist.truck?.make,
                checklist.truck?.model
            ].filter(Boolean).some(v => String(v).toLowerCase().includes(text));
            const matchesReviewed = wkReviewed === 'all' || (wkReviewed === 'reviewed' ? Boolean(checklist.reviewedBy) : !checklist.reviewedBy);
            const startTs = new Date(checklist.weekStartDate).getTime();
            const fromOk = !wkDateFrom || startTs >= new Date(wkDateFrom).setHours(0,0,0,0);
            const toOk = !wkDateTo || startTs <= new Date(wkDateTo).setHours(23,59,59,999);
            return matchesText && matchesReviewed && fromOk && toOk;
        });

        return (
            <>
            <Stack spacing={1.5} sx={{ mb: 2 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    <TextField size="small" placeholder="Search driver, truck #, make/model..." value={wkSearch} onChange={(e)=>setWkSearch(e.target.value)} sx={{ flex: 1 }} />
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel>Reviewed</InputLabel>
                        <Select label="Reviewed" value={wkReviewed} onChange={(e)=>setWkReviewed(e.target.value)}>
                            <MenuItem value="all">All</MenuItem>
                            <MenuItem value="reviewed">Reviewed</MenuItem>
                            <MenuItem value="pending">Pending Review</MenuItem>
                        </Select>
                    </FormControl>
                </Stack>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                        <DatePicker label="Week start from" value={wkDateFrom} onChange={setWkDateFrom} slotProps={{ textField: { size: 'small', fullWidth: true } }} />
                        <DatePicker label="Week start to" value={wkDateTo} onChange={setWkDateTo} slotProps={{ textField: { size: 'small', fullWidth: true } }} />
                        <Button size="small" onClick={()=>{ setWkSearch(''); setWkReviewed('all'); setWkDateFrom(null); setWkDateTo(null); }}>Clear Filters</Button>
                        <Button size="small" variant="outlined" startIcon={<PrintIcon />} onClick={() => { setPrintSeed(null); setPrintOpen(true); }}>Print</Button>
                    </Stack>
                </LocalizationProvider>
            </Stack>
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Driver</TableCell>
                            <TableCell>Truck</TableCell>
                            <TableCell>Week Period</TableCell>
                            <TableCell>Overall Status</TableCell>
                            <TableCell>Mileage</TableCell>
                            <TableCell>Submitted</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {!ready ? renderSkeletonRows(7) : filteredChecklists.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center">
                                    <Typography color="text.secondary">No weekly checklists found</Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            filteredChecklists.map((checklist) => (
                                <TableRow 
                                    key={checklist._id}
                                    hover
                                    onClick={() => { markSeen('checklist', checklist._id); handleCardClick(checklist, 'checklist'); }}
                                    sx={{ 
                                        cursor: 'pointer',
                                        borderLeft: (!checklist.reviewedBy || showNewBadge('checklist', checklist._id, (checklist.submittedAt || checklist.createdAt))) ? '4px solid' : '4px solid transparent',
                                        borderLeftColor: !checklist.reviewedBy ? 'warning.main' : (showNewBadge('checklist', checklist._id, (checklist.submittedAt || checklist.createdAt)) ? 'info.main' : 'transparent'),
                                        bgcolor: !checklist.reviewedBy ? 'warning.50' : (showNewBadge('checklist', checklist._id, (checklist.submittedAt || checklist.createdAt)) ? 'info.50' : 'inherit')
                                    }}
                                >
                                    <TableCell>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <Avatar sx={{ width: 32, height: 32 }}>
                                                {checklist.submittedBy?.name?.[0] || '?'}
                                            </Avatar>
                                            <Box>
                                                <Typography variant="body2">{checklist.submittedBy?.name || 'Unknown'}</Typography>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                <Typography variant="caption" color="text.secondary">{checklist.submittedBy?.email}</Typography>
                                                    {showNewBadge('checklist', checklist._id, (checklist.submittedAt || checklist.createdAt)) && (
                                                        <Chip label="New" size="small" color="info" />
                                                    )}
                                                    {flaggedSourceIds.has(`weekly_checklist:${checklist._id}`) && (
                                                        <Chip
                                                            label="Needs a look"
                                                            size="small"
                                                            color="warning"
                                                            onClick={(event) => {
                                                                event.stopPropagation();
                                                                handleCardClick(checklist, 'checklist');
                                                            }}
                                                        />
                                                    )}
                                                </Stack>
                                            </Box>
                                        </Stack>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {checklist.truck ? (
                                                (() => {
                                                    const idPart = checklist.truck.truckNumber || checklist.truck.unitNumber || '';
                                                    const makeModel = [checklist.truck.make, checklist.truck.model].filter(Boolean).join(' ');
                                                    if (idPart && makeModel) return `${idPart} - ${makeModel}`;
                                                    if (idPart) return idPart;
                                                    if (makeModel) return makeModel;
                                                    return 'N/A';
                                                })()
                                            ) : 'N/A'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {new Date(checklist.weekStartDate).toLocaleDateString()} - {new Date(checklist.weekEndDate).toLocaleDateString()}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                            {checklist.reviewedBy ? (
                                                <Chip label="Reviewed" size="small" color="success" />
                                            ) : (
                                                <Chip label="Pending Review" size="small" color="warning" />
                                            )}
                                            {!checklist.reviewedBy && (
                                                <Chip label="Needs Review" size="small" color="warning" variant="outlined" />
                                            )}
                                        </Stack>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {formatTotalWeeklyMiles(checklist)} miles
                                        </Typography>
                                    </TableCell>
                                    <TableCell>{new Date(checklist.createdAt).toLocaleString()}</TableCell>
                                    <TableCell align="right">
                                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                                            {!checklist.reviewedBy ? (
                                            <Tooltip title="Mark as Reviewed">
                                                <IconButton 
                                                    size="small" 
                                                    color="primary"
                                                    onClick={async (e) => {
                                                        e.stopPropagation();
                                                        try {
                                                            await apiClient.put(`/forms/weekly-checklist/${checklist._id}`, {});
                                                            fetchData();
                                                        } catch (err) {
                                                            console.error('Error marking as reviewed:', err);
                                                        }
                                                    }}
                                                >
                                                    <AssignmentTurnedInIcon />
                                                </IconButton>
                                            </Tooltip>
                                            ) : (
                                                <Tooltip title="Unmark Reviewed">
                                                    <IconButton
                                                        size="small"
                                                        color="warning"
                                                        onClick={async (e) => {
                                                            e.stopPropagation();
                                                            try {
                                                                await apiClient.post(`/forms/weekly-checklist/${checklist._id}/unreview`);
                                                                fetchData();
                                                            } catch (err) {
                                                                console.error('Error unmarking reviewed:', err);
                                                            }
                                                        }}
                                                    >
                                                        <CancelIcon />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        <Tooltip title="Delete checklist">
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={async (e) => {
                                                    e.stopPropagation();
                                                    if (!window.confirm('Delete this weekly checklist? This cannot be undone.')) return;
                                                    try {
                                                        await apiClient.delete(`/forms/weekly-checklist/${checklist._id}`);
                                                        fetchData();
                                                    } catch (err) {
                                                        console.error('Error deleting checklist:', err);
                                                        setError(err?.response?.data?.message || 'Failed to delete checklist.');
                                                    }
                                                }}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Tooltip>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            </>
        );
    };

    const renderDetailDialog = () => {
        if (!detailItem) return null;

        if (detailType === 'timeoff') {
            return (
                <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
                    <DialogTitle>Time Off Request Details</DialogTitle>
                    <DialogContent>
                        <Stack spacing={3} sx={{ mt: 2 }}>
                            <Box>
                                <Typography variant="subtitle2" color="text.secondary">Employee</Typography>
                                <Typography variant="body1">{detailItem.submittedBy?.name} ({detailItem.submittedBy?.email})</Typography>
                            </Box>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Typography variant="subtitle2" color="text.secondary">Request Type</Typography>
                                    <Chip label={getRequestTypeLabel(detailItem.requestType)} />
                                </Grid>
                                {detailItem.requestType === 'vacation_paid' && detailItem.paidVacationDays != null && (
                                    <Grid item xs={6}>
                                        <Typography variant="subtitle2" color="text.secondary">Paid Vacation Days</Typography>
                                        <Typography variant="body1">
                                            {detailItem.paidVacationDays} day{detailItem.paidVacationDays !== 1 ? 's' : ''}
                                        </Typography>
                                    </Grid>
                                )}
                                <Grid item xs={6}>
                                    <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                                    <Chip 
                                        label={detailItem.status} 
                                        color={
                                            detailItem.status === 'approved' ? 'success' :
                                            detailItem.status === 'denied' ? 'error' :
                                            'warning'
                                        }
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        {isOfficeTimeOffRequest(detailItem) ? 'From' : (detailItem.isPartialDay ? 'Date of Absence' : 'Start Date')}
                                    </Typography>
                                    <Typography variant="body1">{new Date(detailItem.startDate).toLocaleDateString()}</Typography>
                                </Grid>
                                {!(detailItem.isPartialDay && !isOfficeTimeOffRequest(detailItem)) && (
                                    <Grid item xs={6}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            {isOfficeTimeOffRequest(detailItem) ? 'To' : 'End Date'}
                                        </Typography>
                                        <Typography variant="body1">{new Date(detailItem.endDate).toLocaleDateString()}</Typography>
                                    </Grid>
                                )}
                                {isOfficeTimeOffRequest(detailItem) && (
                                    <Grid item xs={6}>
                                        <Typography variant="subtitle2" color="text.secondary">Day type</Typography>
                                        <Typography variant="body1">{detailItem.isPartialDay ? 'Partial Day' : 'All Day'}</Typography>
                                    </Grid>
                                )}
                                {detailItem.isPartialDay && detailItem.partialDayStartTime && (
                                    <>
                                        <Grid item xs={6}>
                                            <Typography variant="subtitle2" color="text.secondary">Leave at</Typography>
                                            <Typography variant="body1">{detailItem.partialDayStartTime}</Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="subtitle2" color="text.secondary">Back at</Typography>
                                            <Typography variant="body1">{detailItem.partialDayEndTime}</Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="subtitle2" color="text.secondary">Duration</Typography>
                                            <Typography variant="body1">
                                                {detailItem.partialDayHours} hour{detailItem.partialDayHours !== 1 ? 's' : ''}
                                            </Typography>
                                        </Grid>
                                    </>
                                )}
                                {!isOfficeTimeOffRequest(detailItem) && (detailItem.leavingYardAt || detailItem.backToYardAt) && (
                                    <>
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle2" color="text.secondary">Terminal Schedule</Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="subtitle2" color="text.secondary">Date to Be In</Typography>
                                            <Typography variant="body1">
                                                {detailItem.leavingYardAt
                                                    ? formatDateTimeCentral(detailItem.leavingYardAt)
                                                    : '—'}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="subtitle2" color="text.secondary">Date to Leave Back Out</Typography>
                                            <Typography variant="body1">
                                                {detailItem.backToYardAt
                                                    ? formatDateTimeCentral(detailItem.backToYardAt)
                                                    : '—'}
                                            </Typography>
                                        </Grid>
                                    </>
                                )}
                            </Grid>
                            {detailItem.reason && (
                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary">Reason</Typography>
                                    <Typography variant="body1">{detailItem.reason}</Typography>
                                </Box>
                            )}
                            {detailItem.reviewNotes && (
                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary">Review Notes</Typography>
                                    <Typography variant="body1">{detailItem.reviewNotes}</Typography>
                                </Box>
                            )}
                            {detailItem.cancellationRequested && (
                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary">Cancellation Request</Typography>
                                    <Typography variant="body1" sx={{ mb: 0.5 }}>
                                        {detailItem.cancellationReason || 'No reason provided.'}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Requested {new Date(detailItem.cancellationRequestedAt).toLocaleString()} by {detailItem.cancellationRequestedBy?.name || 'Requestor'}
                                    </Typography>
                                </Box>
                            )}
                            {detailItem.reviewedBy && (
                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary">Reviewed By</Typography>
                                    <Typography variant="body1">{detailItem.reviewedBy?.name} on {new Date(detailItem.reviewedAt).toLocaleString()}</Typography>
                                </Box>
                            )}
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        {detailItem.status === 'pending' ? (
                            <>
                                <Button 
                                    onClick={() => {
                                        handleUpdateTimeOff(detailItem._id, 'approved');
                                        setDetailOpen(false);
                                    }} 
                                    color="success" 
                                    variant="contained"
                                >
                                    Approve
                                </Button>
                                <Button 
                                    onClick={() => {
                                        handleUpdateTimeOff(detailItem._id, 'denied');
                                        setDetailOpen(false);
                                    }} 
                                    color="error"
                                    variant="contained"
                                >
                                    Deny
                                </Button>
                            </>
                        ) : (
                            <Button 
                                startIcon={<EditIcon />}
                                onClick={() => {
                                    openDecisionDialog(detailItem);
                                    setDetailOpen(false);
                                }}
                            >
                                Change decision
                            </Button>
                        )}
                        <Button
                            color="error"
                            startIcon={<DeleteIcon />}
                            onClick={() => openDeleteTimeOffDialog(detailItem)}
                        >
                            Delete
                        </Button>
                        <Button onClick={() => setDetailOpen(false)}>Close</Button>
                    </DialogActions>
                </Dialog>
            );
        }

        if (detailType === 'apparel') {
            return (
                <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
                    <DialogTitle>Apparel Order Details</DialogTitle>
                    <DialogContent>
                        <Stack spacing={3} sx={{ mt: 2 }}>
                            <Box>
                                <Typography variant="subtitle2" color="text.secondary">Employee</Typography>
                                <Typography variant="body1">{detailItem.submittedBy?.name} ({detailItem.submittedBy?.email})</Typography>
                            </Box>
                            <Grid container spacing={2}>
                                <Grid item xs={detailItem.urgency ? 6 : 12}>
                                    <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                                    <Chip label={detailItem.status} />
                                </Grid>
                                {detailItem.urgency ? (
                                    <Grid item xs={6}>
                                        <Typography variant="subtitle2" color="text.secondary">Urgency</Typography>
                                        <Chip label={detailItem.urgency} variant="outlined" />
                                    </Grid>
                                ) : null}
                            </Grid>
                            <Box>
                                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>Items Ordered</Typography>
                                {detailItem.items?.map((item, index) => (
                                    <Paper key={index} sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={6} md={4}>
                                                <Typography variant="caption" color="text.secondary">Shirt Type & Item #</Typography>
                                                <Typography variant="body2">{item.shirtTypeItemNumber || item.itemType || '—'}</Typography>
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={4}>
                                                <Typography variant="caption" color="text.secondary">Shirt Color</Typography>
                                                <Typography variant="body2">{item.shirtColor || item.color || '—'}</Typography>
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={4}>
                                                <Typography variant="caption" color="text.secondary">Lettering Color</Typography>
                                                <Typography variant="body2">{item.letteringColor || '—'}</Typography>
                                            </Grid>
                                            <Grid item xs={6} sm={4} md={2}>
                                                <Typography variant="caption" color="text.secondary">Size</Typography>
                                                <Typography variant="body2">{item.size || '—'}</Typography>
                                            </Grid>
                                            <Grid item xs={6} sm={4} md={2}>
                                                <Typography variant="caption" color="text.secondary">Size Type</Typography>
                                                <Typography variant="body2">{item.sizeType || 'Regular'}</Typography>
                                            </Grid>
                                            <Grid item xs={6} sm={4} md={2}>
                                                <Typography variant="caption" color="text.secondary">Quantity</Typography>
                                                <Typography variant="body2">{item.quantity}</Typography>
                                            </Grid>
                                            {item.notes && (
                                                <Grid item xs={12}>
                                                    <Typography variant="caption" color="text.secondary">Notes</Typography>
                                                    <Typography variant="body2">{item.notes}</Typography>
                                                </Grid>
                                            )}
                                        </Grid>
                                    </Paper>
                                ))}
                            </Box>
                            {detailItem.shippingAddress && (
                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary">Shipping Address</Typography>
                                    <Typography variant="body1">
                                        {detailItem.shippingAddress.street}<br />
                                        {detailItem.shippingAddress.city}, {detailItem.shippingAddress.state} {detailItem.shippingAddress.zipCode}
                                    </Typography>
                                </Box>
                            )}
                            {detailItem.specialInstructions && (
                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary">Special Instructions</Typography>
                                    <Typography variant="body1">{detailItem.specialInstructions}</Typography>
                                </Box>
                            )}
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDetailOpen(false)}>Close</Button>
                    </DialogActions>
                </Dialog>
            );
        }

        if (detailType === 'checklist') {
            const checklistFlags = openFlags.filter((flag) => flag.source === 'weekly_checklist' && String(flag.sourceId) === String(detailItem?._id));
            const focus = primarySpot(checklistFlags);
            return (
                <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="lg" fullWidth>
                    <DialogTitle>
                        {detailItem ? `Weekly Checklist • ${new Date(detailItem.weekStartDate).toLocaleDateString()} - ${new Date(detailItem.weekEndDate).toLocaleDateString()}` : 'Weekly Checklist Details'}
                    </DialogTitle>
                    <DialogContent>
                        {detailItem && (
                            <Box>
                                <Grid container spacing={3}>
                                    {/* Basic Information */}
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="h6" gutterBottom>Basic Information</Typography>
                                        <Stack spacing={2}>
                                            <Box>
                                                <Typography variant="subtitle2">Driver:</Typography>
                                                <Typography variant="body2">{detailItem.submittedBy?.name} ({detailItem.submittedBy?.email})</Typography>
                            </Box>
                                            <Box>
                                                <Typography variant="subtitle2">Truck:</Typography>
                                                <Typography variant="body2">
                                                    {(detailItem.truck?.truckNumber || detailItem.truck?.unitNumber || '—')}
                                                    {(detailItem.truck?.make || detailItem.truck?.model || detailItem.truck?.year) && (
                                                        <>
                                                            {` ${detailItem.truck?.make || ''} ${detailItem.truck?.model || ''}`.trim()}
                                                            {detailItem.truck?.year ? ` (${detailItem.truck.year})` : ''}
                                                        </>
                                                    )}
                                    </Typography>
                                            </Box>
                                            {detailItem.trailer && (detailItem.trailer.truckNumber || detailItem.trailer.unitNumber || detailItem.trailer.make || detailItem.trailer.model || detailItem.trailer.year) && (
                                                <Box>
                                                    <Typography variant="subtitle2">Trailer:</Typography>
                                                    <Typography variant="body2">
                                                        {detailItem.trailer?.truckNumber || detailItem.trailer?.unitNumber || '—'}
                                                        {(detailItem.trailer?.make || detailItem.trailer?.model || detailItem.trailer?.year) && (
                                                            <>
                                                                {` ${detailItem.trailer?.make || ''} ${detailItem.trailer?.model || ''}`.trim()}
                                                                {detailItem.trailer?.year ? ` (${detailItem.trailer.year})` : ''}
                                                            </>
                                                        )}
                                                    </Typography>
                                                </Box>
                                            )}
                                            <FlagFocus spot="week" flags={checklistFlags} scroll={focus === 'week'}>
                                                <Box>
                                                    <Typography variant="subtitle2">Week Period:</Typography>
                                                    <Typography variant="body2">
                                                        {new Date(detailItem.weekStartDate).toLocaleDateString()} - {new Date(detailItem.weekEndDate).toLocaleDateString()}
                                                    </Typography>
                                                </Box>
                                            </FlagFocus>
                                            {detailItem.reviewedBy && (
                                                <Box>
                                                    <Typography variant="subtitle2">Reviewed By:</Typography>
                                                    <Typography variant="body2">
                                                        {detailItem.reviewedBy.name} on {new Date(detailItem.reviewedAt).toLocaleDateString()}
                                    </Typography>
                                                </Box>
                                            )}
                                        </Stack>
                                    </Grid>

                                    {/* Operational Data */}
                                    <Grid item xs={12} md={6}>
                                        <FlagFocus spot="miles" flags={checklistFlags} scroll={focus === 'miles'}>
                                        <FlagFocus spot="odometer" flags={checklistFlags} scroll={focus === 'odometer'}>
                                        <Typography variant="h6" gutterBottom>Operational Data</Typography>
                                        <Grid container spacing={1.5}>
                                            {Array.isArray(detailItem.truckMileages) && detailItem.truckMileages.length > 0 ? (
                                                <>
                                                    {detailItem.truckMileages.map((row, idx) => {
                                                        const label = mileageRowTruckLabel(row, idx, detailItem);
                                                        const leg = Number(row.mileageEnd) - Number(row.mileageStart);
                                                        return (
                                                            <Grid item xs={12} key={idx}>
                                                                <Paper sx={{ p: 1.5 }}>
                                                                    <Typography variant="caption" color="text.secondary">{label}</Typography>
                                                                    <Typography variant="body2" fontWeight={600}>
                                                                        {row.mileageStart ?? '—'} → {row.mileageEnd ?? '—'}
                                                                        {Number.isFinite(leg) ? ` (${leg.toFixed(1)} mi)` : ''}
                                                                    </Typography>
                                                                </Paper>
                                                            </Grid>
                                                        );
                                                    })}
                                                    <Grid item xs={12} sm={6} md={4}>
                                                        <Paper sx={{ p: 1.5 }}>
                                                            <Typography variant="caption" color="text.secondary">Total miles (all trucks)</Typography>
                                                            <Typography variant="body1" fontWeight={700}>{formatTotalWeeklyMiles(detailItem)} mi</Typography>
                                                        </Paper>
                                                    </Grid>
                                                </>
                                            ) : (
                                                <>
                                                    <Grid item xs={12} sm={6} md={4}>
                                                        <Paper sx={{ p: 1.5 }}>
                                                            <Typography variant="caption" color="text.secondary">Start Odometer</Typography>
                                                            <Typography variant="body1" fontWeight={700}>{detailItem.mileageStart ?? '—'}</Typography>
                                                        </Paper>
                                                    </Grid>
                                                    <Grid item xs={12} sm={6} md={4}>
                                                        <Paper sx={{ p: 1.5 }}>
                                                            <Typography variant="caption" color="text.secondary">End Odometer</Typography>
                                                            <Typography variant="body1" fontWeight={700}>{detailItem.mileageEnd ?? '—'}</Typography>
                                                        </Paper>
                                                    </Grid>
                                                    <Grid item xs={12} sm={6} md={4}>
                                                        <Paper sx={{ p: 1.5 }}>
                                                            <Typography variant="caption" color="text.secondary">Miles Driven</Typography>
                                                            <Typography variant="body1" fontWeight={700}>{getTotalWeeklyMiles(detailItem).toFixed(1)} mi</Typography>
                                                        </Paper>
                                                    </Grid>
                                                </>
                                            )}
                                </Grid>
                                        </FlagFocus>
                                        </FlagFocus>
                            </Grid>
                            
                                    {/* Operational Details */}
                                    {(detailItem.oversizeLoadedMiles || typeof detailItem.dotInspectionCompleted === 'boolean' || detailItem.hasReimbursement || detailItem.cashReceiptsAmount || (detailItem.cashReceiptsAttachments && detailItem.cashReceiptsAttachments.length) || detailItem.hasBreakdownPay || (detailItem.breakdownEvents && detailItem.breakdownEvents.length) || (detailItem.detentionEvents && detailItem.detentionEvents.length) || detailItem.weekendPay === 'half' || detailItem.weekendPay === 'full' || (detailItem.weekendDispatchDates && detailItem.weekendDispatchDates.length) || (detailItem.weeklyLoads && detailItem.weeklyLoads.length)) && (
                                        <Grid item xs={12}>
                                            <Typography variant="h6" gutterBottom>Operational Details</Typography>
                                            <Grid container spacing={1.5}>
                                                {typeof detailItem.oversizeLoadedMiles === 'number' && detailItem.oversizeLoadedMiles > 0 && (
                                                    <Grid item xs={12} sm={6} md={3}>
                                                        <Paper sx={{ p: 1.5 }}>
                                                            <Typography variant="caption" color="text.secondary">Loaded Oversize Miles</Typography>
                                                            <Typography variant="body1" fontWeight={700}>{detailItem.oversizeLoadedMiles}</Typography>
                                                        </Paper>
                                        </Grid>
                                                )}
                                                {typeof detailItem.dotInspectionCompleted === 'boolean' && (
                                                    <Grid item xs={12} sm={6} md={3}>
                                                        <Paper sx={{ p: 1.5 }}>
                                                            <Typography variant="caption" color="text.secondary">DOT Inspection</Typography>
                                                            <Typography variant="body1" fontWeight={700}>{detailItem.dotInspectionCompleted ? 'Completed' : 'Not Completed'}</Typography>
                                                        </Paper>
                                </Grid>
                                                )}
                                                {detailItem.hasReimbursement && (
                                                    <Grid item xs={12} sm={6} md={3}>
                                                        <Paper sx={{ p: 1.5 }}>
                                                            <Typography variant="caption" color="text.secondary">Reimbursements this week</Typography>
                                                            <Typography variant="body1" fontWeight={700}>Yes</Typography>
                                                        </Paper>
                                                    </Grid>
                                                )}
                                                {(typeof detailItem.cashReceiptsAmount === 'number' && detailItem.cashReceiptsAmount > 0) && (
                                                    <Grid item xs={12} sm={6} md={3}>
                                                        <Paper sx={{ p: 1.5 }}>
                                                            <Typography variant="caption" color="text.secondary">Cash Receipts / Reimbursement</Typography>
                                                            <Typography variant="body1" fontWeight={700}>${detailItem.cashReceiptsAmount.toFixed(2)}</Typography>
                                                        </Paper>
                                                    </Grid>
                                                )}
                                                {detailItem.cashReceiptsNote && (
                                                    <Grid item xs={12}>
                                                        <Paper sx={{ p: 1.5 }}>
                                                            <Typography variant="caption" color="text.secondary">Cash Receipts Note</Typography>
                                                            <Typography variant="body2">{detailItem.cashReceiptsNote}</Typography>
                                                        </Paper>
                                                    </Grid>
                                                )}
                                                {Array.isArray(detailItem.cashReceiptsAttachments) && detailItem.cashReceiptsAttachments.length > 0 && (
                                                    <Grid item xs={12}>
                                                        <Typography variant="caption" color="text.secondary">Receipt photos</Typography>
                                                        <ReceiptAttachmentGallery
                                                            attachments={detailItem.cashReceiptsAttachments}
                                                            ownerId={detailItem._id}
                                                            kind="checklist"
                                                        />
                                                    </Grid>
                                                )}
                                            </Grid>

                                            {(detailItem.hasBreakdownPay || (Array.isArray(detailItem.breakdownEvents) && detailItem.breakdownEvents.length > 0)) && (
                                                <Box sx={{ mt: 2 }}>
                                                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Breakdown pay</Typography>
                                                    <Grid container spacing={1.5} sx={{ mb: 1 }}>
                                                        <Grid item xs={12} sm={6} md={3}>
                                                            <Paper sx={{ p: 1.5 }}>
                                                                <Typography variant="caption" color="text.secondary">Breakdown pay this week</Typography>
                                                                <Typography variant="body1" fontWeight={700}>{detailItem.hasBreakdownPay ? 'Yes' : 'No'}</Typography>
                                                            </Paper>
                                                        </Grid>
                                                    </Grid>
                                                    {Array.isArray(detailItem.breakdownEvents) && detailItem.breakdownEvents.length > 0 && (
                                                        <TableContainer component={Paper}>
                                                            <Table size="small">
                                                                <TableHead>
                                                                    <TableRow>
                                                                        <TableCell>Date</TableCell>
                                                                        <TableCell>Time of Notification</TableCell>
                                                                    </TableRow>
                                                                </TableHead>
                                                                <TableBody>
                                                                    {detailItem.breakdownEvents.map((ev, i) => (
                                                                        <TableRow key={i}>
                                                                            <TableCell>{ev?.date ? new Date(ev.date).toLocaleDateString() : '—'}</TableCell>
                                                                            <TableCell>{ev?.notificationTime || '—'}</TableCell>
                                                                        </TableRow>
                                                                    ))}
                                                                </TableBody>
                                                            </Table>
                                                        </TableContainer>
                                                    )}
                                                </Box>
                                            )}

                                            {/* Detention Events */}
                                            {Array.isArray(detailItem.detentionEvents) && detailItem.detentionEvents.length > 0 && (
                                                <FlagFocus spot="detention" flags={checklistFlags} scroll={focus === 'detention'} sx={{ mt: 2 }}>
                                                <Box>
                                                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Detention</Typography>
                                                    <TableContainer component={Paper}>
                                                        <Table size="small">
                                                            <TableHead>
                                                                <TableRow>
                                                                    <TableCell>Date</TableCell>
                                                                    <TableCell>Location</TableCell>
                                                                    <TableCell>Arrival</TableCell>
                                                                    <TableCell>Depart</TableCell>
                                                                </TableRow>
                                                            </TableHead>
                                                            <TableBody>
                                                                {detailItem.detentionEvents.map((ev, i) => (
                                                                    <TableRow key={i}>
                                                                        <TableCell>{ev?.date ? new Date(ev.date).toLocaleDateString() : '—'}</TableCell>
                                                                        <TableCell>{ev?.location || '—'}</TableCell>
                                                                        <TableCell>{ev?.arrivalTime || '—'}</TableCell>
                                                                        <TableCell>{ev?.departTime || '—'}</TableCell>
                                                                    </TableRow>
                                                                ))}
                                                            </TableBody>
                                                        </Table>
                                                    </TableContainer>
                                                </Box>
                                                </FlagFocus>
                                            )}

                                            {(detailItem.weekendPay === 'half' || detailItem.weekendPay === 'full') && (
                                                <Box sx={{ mt: 2 }}>
                                                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Weekend Pay</Typography>
                                                    <Paper sx={{ p: 1.5, display: 'inline-block' }}>
                                                        <Typography variant="body1" fontWeight={700}>
                                                            {detailItem.weekendPay === 'half' ? 'Half Weekend' : 'Full Weekend'}
                                                        </Typography>
                                                    </Paper>
                                                </Box>
                                            )}

                                            {Array.isArray(detailItem.weekendDispatchDates) && detailItem.weekendDispatchDates.length > 0 && detailItem.weekendPay !== 'half' && detailItem.weekendPay !== 'full' && (
                                                <Box sx={{ mt: 2 }}>
                                                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Weekend Dispatch</Typography>
                                                    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                                                        {detailItem.weekendDispatchDates.map((d, i) => (
                                                            <Chip key={i} label={d ? new Date(d).toLocaleDateString() : '—'} size="small" />
                                                        ))}
                                                    </Stack>
                                                </Box>
                                            )}
                            
                                            {/* Weekly Loads */}
                                            {Array.isArray(detailItem.weeklyLoads) && detailItem.weeklyLoads.length > 0 && (
                                                <FlagFocus spot="loads" flags={checklistFlags} scroll={focus === 'loads'} sx={{ mt: 2 }}>
                                                <Box>
                                                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Weekly Loads</Typography>
                                                    <TableContainer component={Paper}>
                                                        <Table size="small">
                                                            <TableHead>
                                                                <TableRow>
                                                                    <TableCell>Date</TableCell>
                                                                    <TableCell>ELD Order #</TableCell>
                                                                    <TableCell>Pickup City/State</TableCell>
                                                                    <TableCell>Delivery City/State</TableCell>
                                                                </TableRow>
                                                            </TableHead>
                                                            <TableBody>
                                                                {detailItem.weeklyLoads.map((row, i) => (
                                                                    <TableRow key={i}>
                                                                        <TableCell>{row?.date ? new Date(row.date).toLocaleDateString() : '—'}</TableCell>
                                                                        <TableCell>{row?.eldOrderNumber || '—'}</TableCell>
                                                                        <TableCell>{row?.from || '—'}</TableCell>
                                                                        <TableCell>{row?.to || '—'}</TableCell>
                                                                    </TableRow>
                                                                ))}
                                                            </TableBody>
                                                        </Table>
                                                    </TableContainer>
                                                </Box>
                                                </FlagFocus>
                            )}
                                        </Grid>
                                    )}

                                    {/* Weekly Metrics */}
                                    {detailItem.dailyMetrics && (
                                        <Grid item xs={12}>
                                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                                <Typography variant="h6">Weekly Metrics</Typography>
                                                <Typography variant="body2" color="error" fontWeight={700} sx={{ color: (theme) => `${theme.palette.error.main} !important` }}>
                                                    * Entries in the table below must be recorded accurately on this checklist. Unrecorded items will not be reflected on paychecks.
                                                </Typography>
                        </Stack>
                                            <TableContainer component={Paper}>
                                                <Table size="small">
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell>Metric</TableCell>
                                                            {['Sun','Mon','Tues','Wed','Thurs','Fri','Sat'].map((d) => (
                                                                <TableCell key={d} align="center">{d}</TableCell>
                                                            ))}
                                                            <TableCell align="center">Total</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        <TableRow>
                                                            <TableCell>Nights Out</TableCell>
                                                            {(detailItem.dailyMetrics.nightsOut || Array(7).fill(false)).map((v, i) => (
                                                                <TableCell key={i} align="center">{v ? '✓' : '-'}</TableCell>
                                                            ))}
                                                            <TableCell align="center"><strong>{(detailItem.dailyMetrics.nightsOut || []).filter(Boolean).length}</strong></TableCell>
                                                        </TableRow>
                                                        <TableRow>
                                                            <TableCell>Tarps</TableCell>
                                                            {(detailItem.dailyMetrics.tarps || Array(7).fill('')).map((v, i) => (
                                                                <TableCell key={i} align="center">{formatTarpCell(v)}</TableCell>
                                                            ))}
                                                            <TableCell align="center"><strong>{formatTarpTotal(sumTarps(detailItem.dailyMetrics.tarps))}</strong></TableCell>
                                                        </TableRow>
                                                        <TableRow>
                                                            <TableCell>
                                                                <Stack spacing={0}>
                                                                    <span>Extra drop/pickup</span>
                                                                    <Typography variant="caption" color="text.secondary">*Additional Docks for TG Lanes</Typography>
                                                                </Stack>
                                                            </TableCell>
                                                            {(detailItem.dailyMetrics.extraDropPickup || Array(7).fill('')).map((v, i) => (
                                                                <TableCell key={i} align="center">{v || '-'}</TableCell>
                                                            ))}
                                                            <TableCell align="center"><strong>{(detailItem.dailyMetrics.extraDropPickup || []).reduce((t, v) => t + (parseInt(v, 10) || 0), 0)}</strong></TableCell>
                                                        </TableRow>
                                                        <TableRow>
                                                            <TableCell>WW Wood Cabinet Drops</TableCell>
                                                            {(detailItem.dailyMetrics.wwWoodCabinetDrops || Array(7).fill('')).map((v, i) => (
                                                                <TableCell key={i} align="center">{v || '-'}</TableCell>
                                                            ))}
                                                            <TableCell align="center"><strong>{(detailItem.dailyMetrics.wwWoodCabinetDrops || []).reduce((t, v) => t + (parseInt(v, 10) || 0), 0)}</strong></TableCell>
                                                        </TableRow>
                                                        <TableRow>
                                                            <TableCell>
                                                                <Stack spacing={0}>
                                                                    <span>Pre-Load (Not Delivering)</span>
                                                                    <Typography variant="caption" color="text.secondary">*Pickup & drop within 160 miles of yard; trailer moves lot-to-lot</Typography>
                                                                </Stack>
                                                            </TableCell>
                                                            {(detailItem.dailyMetrics.preLoadNotDelivering || Array(7).fill('')).map((v, i) => (
                                                                <TableCell key={i} align="center">{v || '-'}</TableCell>
                                                            ))}
                                                            <TableCell align="center"><strong>{(detailItem.dailyMetrics.preLoadNotDelivering || []).reduce((t, v) => t + (parseInt(v, 10) || 0), 0)}</strong></TableCell>
                                                        </TableRow>
                                                        <TableRow>
                                                            <TableCell>
                                                                <Stack spacing={0}>
                                                                    <span>Oversize/Truss</span>
                                                                    <Typography variant="caption" color="text.secondary">*with permits</Typography>
                                                                </Stack>
                                                            </TableCell>
                                                            {(detailItem.dailyMetrics.oversizeTruss || Array(7).fill(false)).map((v, i) => (
                                                                <TableCell key={i} align="center">{v ? '✓' : '-'}</TableCell>
                                                            ))}
                                                            <TableCell align="center"><strong>{(detailItem.dailyMetrics.oversizeTruss || []).filter(Boolean).length}</strong></TableCell>
                                                        </TableRow>
                                                        <TableRow>
                                                            <TableCell>
                                                                <Stack spacing={0}>
                                                                    <span>Short Run (Delivering)</span>
                                                                    <Typography variant="caption" color="text.secondary">*Pickup & delivery within 325 miles</Typography>
                                                                </Stack>
                                                            </TableCell>
                                                            {Array(7).fill(0).map((_, i) => {
                                                                const yesNo = (detailItem.dailyMetrics.shortRunYes || [])[i];
                                                                const orders = (detailItem.dailyMetrics.shortRunOrders || [])[i] || [];
                                                                const legacy = (detailItem.dailyMetrics.shortRunDelivering || [])[i];
                                                                const cell = yesNo ? (yesNo === 'yes' ? (orders.length ? orders.join(', ') : 'Yes') : 'No') : (legacy ? (parseInt(legacy, 10) > 0 ? 'Yes' : 'No') : '—');
                                                                return (<TableCell key={i} align="center">{cell}</TableCell>);
                                                            })}
                                                            <TableCell align="center"><strong>{(detailItem.dailyMetrics.shortRunOrders || []).reduce((t, arr) => t + (Array.isArray(arr) ? arr.filter(o => /\d+/.test(String(o))).length : 0), 0)}</strong></TableCell>
                                                        </TableRow>
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </Grid>
                                    )}

                                    {/* Comments / Notes */}
                                    {detailItem.additionalNotes && (
                                        <Grid item xs={12}>
                                            <Typography variant="h6" gutterBottom>Comments / Notes</Typography>
                                            <Paper sx={{ p: 2, bgcolor: 'grey.100' }}>
                                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{detailItem.additionalNotes}</Typography>
                                            </Paper>
                                        </Grid>
                                    )}
                                </Grid>
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button startIcon={<PrintIcon />} onClick={() => { setPrintSeed(detailItem); setPrintOpen(true); }}>Print</Button>
                        <Button onClick={() => setDetailOpen(false)}>Close</Button>
                    </DialogActions>
                </Dialog>
            );
        }
    };

    return (
        <Container maxWidth="xl" disableGutters sx={{ pt: 0, pb: { xs: 0.5, md: 1 } }}>
            <Paper sx={{ p: 3, mb: 3, borderRadius: 2, borderTop: '3px solid', borderTopColor: 'primary.main' }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
                    <Box>
                        <Typography variant="h4" gutterBottom>Forms Management</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Manage time off requests, apparel orders, and weekly checklists
                        </Typography>
                    </Box>
                    <Tooltip title="Refresh">
                        <IconButton onClick={fetchData} disabled={loading}>
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                </Stack>

                {renderStatsCards()}

                {/* Global search removed; per-tab filter bars below */}

                {error && (
                    <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                    <Tabs value={tabValue} onChange={(e, newValue) => selectTab(newValue)}>
                        <Tab 
                            label={ready ? `Time Off Requests (${timeOffRequests.length})` : 'Time Off Requests'} 
                            icon={<EventAvailableIcon />} 
                            iconPosition="start"
                        />
                        <Tab 
                            label={ready ? `Apparel Orders (${apparelOrders.length})` : 'Apparel Orders'} 
                            icon={<LocalMallIcon />} 
                            iconPosition="start"
                        />
                        <Tab 
                            label={ready ? `Weekly Checklists (${checklists.length})` : 'Weekly Checklists'} 
                            icon={<ChecklistIcon />} 
                            iconPosition="start"
                        />
                    </Tabs>
                </Box>

                <TabPanel value={tabValue} index={0} screen="forms.management.time-off">
                    {renderTimeOffTable()}
                </TabPanel>

                <TabPanel value={tabValue} index={1} screen="forms.management.apparel">
                    {renderApparelTable()}
                </TabPanel>

                <TabPanel value={tabValue} index={2} screen="forms.management.checklists">
                    {renderChecklistTable()}
                </TabPanel>
            </Paper>

            {renderDetailDialog()}

            <WeeklyChecklistPrintDialog
                open={printOpen}
                onClose={() => setPrintOpen(false)}
                checklists={checklists}
                initialChecklist={printSeed}
                defaultFrom={wkDateFrom}
                defaultTo={wkDateTo}
            />

            {/* Apparel status change dialog */}
            <Dialog open={statusDialogOpen} onClose={() => setStatusDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Change order status</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <FormControl fullWidth>
                            <InputLabel id="apparel-status-label">Status</InputLabel>
                            <Select
                                labelId="apparel-status-label"
                                label="Status"
                                value={statusNew}
                                onChange={(e) => setStatusNew(e.target.value)}
                            >
                                <MenuItem value="submitted">Submitted</MenuItem>
                                <MenuItem value="processing">Processing</MenuItem>
                                <MenuItem value="shipped">Shipped</MenuItem>
                                <MenuItem value="delivered">Delivered</MenuItem>
                                <MenuItem value="cancelled">Cancelled</MenuItem>
                            </Select>
                        </FormControl>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setStatusDialogOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={async () => {
                            if (!statusTarget) return;
                            await handleUpdateApparel(statusTarget._id, { status: statusNew });
                            setStatusDialogOpen(false);
                            setStatusTarget(null);
                        }}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete time off confirmation */}
            <Dialog
                open={deleteTimeOffDialogOpen}
                onClose={closeDeleteTimeOffDialog}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Delete time off request?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                        Delete {deleteTimeOffTarget?.label || 'this time off request'}? This cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDeleteTimeOffDialog} disabled={deleteTimeOffSubmitting}>
                        Cancel
                    </Button>
                    <Button
                        color="error"
                        variant="contained"
                        onClick={confirmDeleteTimeOff}
                        disabled={deleteTimeOffSubmitting}
                    >
                        {deleteTimeOffSubmitting ? 'Deleting…' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Decision Edit Dialog */}
            <Dialog open={decisionDialogOpen} onClose={() => setDecisionDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Edit time-off decision</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <FormControl fullWidth>
                            <InputLabel id="decision-status-label">Status</InputLabel>
                            <Select
                                labelId="decision-status-label"
                                value={decisionStatus}
                                label="Status"
                                onChange={(e) => setDecisionStatus(e.target.value)}
                            >
                                <MenuItem value="pending">Pending</MenuItem>
                                <MenuItem value="approved">Approved</MenuItem>
                                <MenuItem value="denied">Denied</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField
                            label="Review notes (optional)"
                            multiline
                            minRows={3}
                            value={decisionNotes}
                            onChange={(e) => setDecisionNotes(e.target.value)}
                            placeholder="Explain why you're changing this decision (recommended)"
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDecisionDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={submitDecisionChange}>Save</Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}

export default FormsManagementPage;
